import { useCallback, useRef, useState, type ChangeEvent } from 'react';

// Max bytes per file accepted by the picker. Keeps the prompt-prefix path
// honest about what we'll inline. Lift this once a real upload endpoint
// lands and large files can stream up.
const MAX_FILE_SIZE_BYTES = 10 * 1024 * 1024;

export const useComposerAttachments = () => {
  const [files, setFiles] = useState<File[]>([]);
  // Names of files rejected by the size cap on the LAST pick — silent drops
  // made users think the attachment went through.
  const [rejectedNames, setRejectedNames] = useState<string[]>([]);
  const inputRef = useRef<HTMLInputElement>(null);

  const openPicker = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleFilesPicked = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const picked = Array.from(event.target.files ?? []);
      const valid = picked.filter((f) => f.size <= MAX_FILE_SIZE_BYTES);
      const rejected = picked.filter((f) => f.size > MAX_FILE_SIZE_BYTES);
      setRejectedNames(rejected.map((f) => f.name));
      setFiles((prev) => [...prev, ...valid]);
      // Reset so the same file can be re-picked after removal.
      event.target.value = '';
    },
    []
  );

  const removeFile = useCallback((index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  }, []);

  const clearFiles = useCallback(() => setFiles([]), []);

  return {
    files,
    rejectedNames,
    inputRef,
    openPicker,
    handleFilesPicked,
    removeFile,
    clearFiles,
  };
};
